import {ManuscriptDisclosure} from '../../components/ai/DisclosureControls';
import React, { useState, useMemo } from 'react';
import {useDispatch} from '../../reduxHooks';
import moment from 'moment';
import styled, { css } from 'styled-components';
import { Media } from 'src/utils/Media';

import { useTranslation } from 'react-i18next';
import { updateSearchTerm } from '../../modules/betaOpportunities';
import CoverRater from './coverRater';
import { bookStatusUtil, imageUtil, uxAnalyticsUtil } from '../../utils';
import { useHistory } from 'react-router';
import { MediaQuery } from '../../constants/size';
import BookFormatBadge from '../../components/book/bookFormatBadge';
import {useDiscoveryExperimentTracking} from './experimentClient';
import AppIcon from '../../components/AppIcon';

const LARGE_WIDTH = 200;
const SMALL_WIDTH = 100;
const DESCRIPTION_MAX_CHARS_LARGE = 200;
const DESCRIPTION_MAX_CHARS_SMALL = 50;

const Wrapper = styled.div<{ large?: boolean }>`
  ${MediaQuery.tablet} {
    display: grid;
    ${({ large }) => css`
      grid-template-columns: minmax(0, ${large ? LARGE_WIDTH : SMALL_WIDTH}px) 1fr;
    `}
    padding: 0px 0px 10px 0px;
  }
`;

const RoundedImage = styled.img`
  border-radius: 10px;
  display: block;
  max-width: 100%;
`;

const CoverContainer = styled.div`
  position: relative;
`;

const CoverButton = styled.button`
  background: none;
  border: 0;
  cursor: pointer;
  display: block;
  padding: 0;

  &:focus-visible { outline: 2px solid var(--br-focus); }
`;

const CoverRaterPanel = styled.div`
  background: var(--br-raised-surface);
  border: 1px solid var(--br-border);
  border-radius: var(--br-control-radius);
  box-shadow: 0 4px 12px #0003;
  display: none;
  left: 0;
  padding: var(--br-space-3);
  position: absolute;
  top: 100%;
  z-index: 2;

  ${CoverContainer}:hover &, ${CoverContainer}:focus-within & { display: block; }
`;

const Badge = styled.span`
  background: var(--br-raised-surface);
  border: 1px solid var(--br-border);
  border-radius: 999px;
  display: inline-block;
  margin: 2px;
  padding: 2px 7px;
`;

const GenreButton = styled.button`
  background: var(--br-raised-surface);
  border: 1px solid var(--br-border);
  border-radius: 999px;
  color: var(--br-text);
  cursor: pointer;
  margin: 2px;
  padding: 2px 7px;

  &:focus-visible { outline: 2px solid var(--br-focus); }
`;

const BookTitle = styled.button`
  background: none;
  border: 0;
  color: var(--br-text);
  cursor: pointer;
  font-size: 1.2em;
  font-weight: 700;
  margin-bottom: 0.4em;
  padding: 0;
  text-align: left;

  &:focus-visible { outline: 2px solid var(--br-focus); }
`;

const InfoContainer = styled.div`
  padding-left: 1rem;
  display: flex;
  flex-direction: column;
`;

type Props = {
  book: any;
  openInReader?: boolean;
  descriptionMaxLength: number;
  showLeaveOption: boolean;
  large?: boolean;
};

const BetaOpportunityItem: React.FC<Props> = ({
  book,
  openInReader,
  descriptionMaxLength,
  showLeaveOption,
  large,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const trackExperiment = useDiscoveryExperimentTracking();
  const { t } = useTranslation();
  const canHover =
    typeof window.matchMedia !== 'function' ||
    window.matchMedia('(hover: hover)').matches;
  const [coverRaterButtonClicked, setCoverRaterButtonClicked] = useState<'like' | 'dislike' | null>(null);
  const defaultCover = 'https://storage.googleapis.com/beta-reader-prod.appspot.com/defaults/DEFAULT_COVER_BACKGROUND.png';

  const descriptionMaxChars = useMemo(
    () => (large ? DESCRIPTION_MAX_CHARS_LARGE : DESCRIPTION_MAX_CHARS_SMALL),
    [large]
  );
  const descriptionText = useMemo(
    () =>
      `${book.description?.substr(0, descriptionMaxChars) ?? ''}${
        book.description?.length > descriptionMaxChars ? '...' : ''
      }`,
    [book.description, descriptionMaxChars]
  );

  const openBook = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    dispatch(updateSearchTerm(''));
    trackExperiment('book_open');
    if (openInReader) {
      history.push(`${process.env.PUBLIC_URL}/read?book=${book._id}`);
    } else {
      history.push(`${process.env.PUBLIC_URL}/books/${book._id}`);
    }
  };

  const selectGenre = (genre: string) => {
    uxAnalyticsUtil.trackEvent({
      category: 'Discover',
      action: `clicked-genre-${genre}`,
      label: 'sliderItem',
    });
    history.push(`/discover/genre/${genre}`);
  };

  const trackCoverRating = (event: any) => {
    if (!canHover) {
      return;
    }

    if (event.type === 'mouseenter') {
      uxAnalyticsUtil.trackEvent({
        category: 'Experiment',
        action: 'Displayed cover-rater',
        label: 'Cover-rater',
      });
      return;
    }
    if (event === 'like' || event === 'dislike') {
      setCoverRaterButtonClicked(event);
      uxAnalyticsUtil.trackEvent({
        category: 'Experiment',
        action: 'Clicked rate cover',
        label: 'Cover-rater',
        value: event === 'like' ? 1 : 0,
      });
    }
  };

  let thumbnail = defaultCover;
  if (book.cover !== undefined && book.cover.highRes !== undefined) {
    thumbnail = book.cover.highRes.url;
  }

  return (
    <Wrapper large={large}>
      <CoverContainer onMouseEnter={thumbnail !== defaultCover ? trackCoverRating : undefined}>
            <div>
              <CoverButton onClick={openBook} aria-label={`${t('Open')} ${book.title}`}>
              <RoundedImage
                alt={`${book.title} cover`}
                className="cover-image"
                src={imageUtil.getImageUrl({
                  operation: `width=${large ? LARGE_WIDTH : SMALL_WIDTH}`,
                  originalImageUrl: thumbnail,
                })}
              />
              </CoverButton>
              {book.pubDate && (
                <span style={{ fontSize: '0.8em' }}>
                  {t('PublicationDate')} {moment(book.pubDate).format('L')}
                </span>
              )}
            </div>
          {thumbnail !== defaultCover && canHover && <CoverRaterPanel>
            <strong>{t('LikeThisCover')}</strong>
            <CoverRater
              coverRaterButtonClicked={coverRaterButtonClicked}
              clickedCoverRating={trackCoverRating}
            />
          </CoverRaterPanel>}
      </CoverContainer>
      <InfoContainer>
        <BookTitle
          className="header-link br-text"
          onClick={openBook}
        >
          {book.title}
        </BookTitle>
        <div style={{ display: 'flex', flexDirection: 'row' }}>
          <span style={{ color: 'var(--br-muted)', flex: 1 }}>
            {`${t('by')} ${book.authorName}`}{' '}
            {book.isPremium && (
              <span title={t('PremiumAuthorInfo')} aria-label={t('PremiumAuthorInfo')}>
                <AppIcon name="star" />
              </span>
            )}
          </span>
          {showLeaveOption && (
            <details style={{ alignSelf: 'flex-end', margin: 'auto' }}>
              <summary aria-label={t('Actions')}><AppIcon name="ellipsis vertical" /></summary>
              <button type="button" onClick={() => history.push(`/exit-survey?book=${book._id}`)}>
                {t('LeaveBeta')}
              </button>
            </details>
          )}
        </div>
        <br />
        <div>
          <span
            style={{ fontSize: '0.9em', color: 'var(--br-muted)', marginRight: '10px' }}
          >
            <span style={{ fontSize: '0.8em' }}>
              {book.wordCount.toLocaleString()} {t('Words', 'words')}
            </span>
          </span>
          <span
            style={{ fontSize: '0.9em', color: 'var(--br-muted)', marginRight: '10px' }}
          >
            <span style={{ fontSize: '0.8em' }}>
              {book.chapterCount.toLocaleString()} {t('Chapters', 'chapters')}
            </span>
          </span>
          <span
            style={{ fontSize: '0.9em', color: 'var(--br-muted)', marginRight: '10px' }}
          >
            <AppIcon name="globe" />{' '}
            <span style={{ fontSize: '0.8em' }}>
              {book.language &&
                book.language.charAt(0).toUpperCase() + book.language.slice(1)}
            </span>
          </span>
        </div>
        <div>
          <ManuscriptDisclosure book={book} />
          <BookFormatBadge audioAvailable={Boolean(book.audioAvailable)} />
          {book.status && (
            <Badge
              title={t(`MSStatusDescription_${book.status}`)}
              style={{ borderColor: bookStatusUtil.getStatusColor(book.status) }}
            >{book.status}</Badge>
          )}
          {book.isMature && (
            <Badge style={{ borderColor: 'var(--br-danger)' }}>
              {t('MatureContent')}
            </Badge>
          )}
        </div>
        <div
          style={{
            color: 'var(--br-muted)',
            marginTop: '0.5em',
          }}
        >
          <Media greaterThan="mobile">
            {() => <p>{descriptionText}</p>}
          </Media>
          <Media at="mobile">
            {(className, renderChildren) =>
              renderChildren && (
                <p
                  style={{
                    fontSize: '0.8em',
                  }}
                >
                  {descriptionText}
                </p>
              )
            }
          </Media>
        </div>
        <div style={{ marginTop: 'auto' }}>
          {book.genres &&
            book.genres.map((genre: any, index: any, array: any) => {
              if (index < 2) {
                return (
                  <GenreButton
                    className="label-link"
                    onClick={() => selectGenre(genre)}
                    key={`${index + genre}`}
                  >{genre}</GenreButton>
                );
              }
              if (array.length === index + 1 && array.length - 2 > 0) {
                return (
                  <GenreButton
                    onClick={openBook}
                    key={`${index + genre}`}
                  >{`+${array.length - 2} ${t('LoadMore')}`}</GenreButton>
                );
              }
              return null;
            })}
        </div>
      </InfoContainer>
    </Wrapper>
  );
};

export default BetaOpportunityItem;
